import {
  bmpType,
  gifType,
  jpegType,
  pdfType,
  pngType,
  tiffType,
  webpType,
} from "@/Utils/fileValidation";

import { flatMap } from "lodash";

import {
  FileSortKey,
  FileStatus,
  type LibraryFile,
  SortDirection,
} from "./types";

export const getDisplayedFiles = ({
  allFiles,
  selectedFolderName,
  selectedViewId,
  searchValue,
  sortKey,
  sortDirection,
}: {
  allFiles: LibraryFile[];
  selectedFolderName: string;
  selectedViewId: FileStatus | null;
  searchValue: string;
  sortKey: FileSortKey;
  sortDirection: SortDirection;
}) => {
  let filteredFiles = allFiles;

  if (searchValue.trim().length) {
    filteredFiles = allFiles.filter((f) =>
      f.name.toLowerCase().includes(searchValue.toLowerCase()),
    );
  } else if (!!selectedFolderName) {
    filteredFiles = allFiles.filter((f) => f.folderName === selectedFolderName);
  } else {
    filteredFiles = allFiles.filter((f) => f.status === selectedViewId);
  }

  if (sortKey === FileSortKey.NAME) {
    return filteredFiles.sort((a, b) =>
      sortDirection === SortDirection.ASC
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name),
    );
  }

  if (sortKey === FileSortKey.FOLDER && !selectedFolderName) {
    return filteredFiles.sort((a, b) =>
      sortDirection === SortDirection.ASC
        ? a.folderName.localeCompare(b.name)
        : b.folderName.localeCompare(a.name),
    );
  }

  return filteredFiles.sort((a, b) =>
    sortDirection === SortDirection.ASC
      ? a.date.getTime() - b.date.getTime()
      : b.date.getTime() - a.date.getTime(),
  );
};

export const getShowFolderColumn = ({
  selectedFolderName,
  selectedViewId,
  searchValue,
}: {
  selectedFolderName?: string;
  selectedViewId: FileStatus | null;
  searchValue: string;
}) => {
  return (
    !selectedFolderName &&
    (!!searchValue || selectedViewId === FileStatus.PUBLISHED)
  );
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.setAttribute("download", fileName);
  link.setAttribute("href", url);
  link.click();
};

export const downloadFile = (file: LibraryFile) => {
  fetch(file.url!)
    .then((response) => response.blob())
    .then((blob) => downloadBlob(blob, file.name));
};

const SAFE_FILE_NAME_REGEX = /^[a-zA-Z0-9 _.()-]+$/;

export const isSafeFileName = (name: string) => SAFE_FILE_NAME_REGEX.test(name);

export const getIsPreviewable = (file: LibraryFile) => {
  const fileExtension = file.format.toLowerCase();
  return flatMap([
    pdfType.extensions,
    jpegType.extensions,
    pngType.extensions,
    gifType.extensions,
    bmpType.extensions,
    webpType.extensions,
    tiffType.extensions,
  ]).includes(fileExtension);
};
